import React, { ReactNode } from 'react';
import * as RadixContextMenu from '@radix-ui/react-context-menu';
import { baseMenuItemStyles, OptionLabel } from '@producer/ui-kit';
import styled from 'styled-components';

import type { Item } from '../dropdown-menu/DropdownMenu';

type ContextMenuProps = {
  items: Item[];
  onOpenChange?: (value: boolean) => void;

  /** The right-clickable area. Must accept a forwarded ref and spread extra props on its DOM root. */
  children: ReactNode;
};

/**
 * Right-click menu sharing the `DropdownMenu` item model and styling. Renders
 * flat action items only (no checkbox / sub-menu support yet).
 */
export const ContextMenu: React.FC<ContextMenuProps> = ({ items, onOpenChange, children }) => {
  const visibleItems = items.filter((item) => !item.hidden);
  const hasIcon = visibleItems.some((item) => !!item.icon);

  if (!visibleItems.length) {
    return children;
  }

  return (
    <RadixContextMenu.Root onOpenChange={onOpenChange}>
      <RadixContextMenu.Trigger asChild>{children}</RadixContextMenu.Trigger>

      <RadixContextMenu.Portal>
        <Content onClick={(e) => e.stopPropagation()}>
          {visibleItems.map((item, index) => (
            <ListItem
              key={`option-${index}`}
              disabled={item.disabled}
              onSelect={item.onSelect}
              data-type={item.type}
            >
              <OptionLabel>
                {hasIcon && item.icon}

                <span>{item.title}</span>
              </OptionLabel>
            </ListItem>
          ))}
        </Content>
      </RadixContextMenu.Portal>
    </RadixContextMenu.Root>
  );
};

const Content = styled(RadixContextMenu.Content)`
  min-width: 18rem;
  border-radius: 0.6rem;
  padding: 0.4rem;
  z-index: calc(var(--layer-modal) + 1);
  box-shadow: rgba(0, 0, 0, 0.2) 0 4px 24px;
  backdrop-filter: blur(10px) saturate(190%) contrast(70%) brightness(80%);
  background-color: rgba(29, 30, 43, 0.498);
`;

const ListItem = styled(RadixContextMenu.Item)`
  ${baseMenuItemStyles}
`;
